import React, { useEffect } from 'react';
import { Col, Form, OverlayTrigger, Tooltip } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCircleQuestion, faCircleInfo, faRotateRight } from '@fortawesome/free-solid-svg-icons';
import DateTimePicker from 'react-datetime-picker';
import Slider from 'rc-slider';
import 'rc-slider/assets/index.css';
import PropTypes from 'prop-types';
import { useAssignmentContext } from '../../pages/assignments/context';
import moment from 'moment';
import {useGetAssignmentSettingMutation} from '../../features/authentication/persistApiSlice';
import AssignmentRetakeSettings from '../AssignmentRetakeSettings/index';

const Index = ({
  allowRetakesOption,
  allowOfflineMode,
  twoColumn,
  headingDescription,
  availabilityRequired,
  modesRequired,
  noColumn,
  randomizedQuestion,
  disabledTextSpeach,
  presetSettingsCallback,
  isSelfAssign,
  isAssessment = false,
  showStudentPrinting
}) => {
  const [getAssignmentSettingsApi] = useGetAssignmentSettingMutation();

  const {
    setAvailabilityOption,
    availabilityOption,
    setAssignmentMode,
    assignmentMode,
    setStudentFileUpload,
    setStartDate,
    setEndDate,
    timedMinute,
    setTimedMinute,
    timedHour,
    setTimedHour,
    randomized,
    setRandomized,
    setTextToSpeech,
    textToSpeech,
    autoLockQuestions,
    setAutoLockQuestions,
    showGrade,
    setShowGrade,
    setAllowLangChange,
    allowLangChange,
    allowAnswerChange,
    setAllowAnswerChange,
    timeOnTaskEnabled,
    setTimeOnTaskEnabled,
    timeOnTaskSeconds,
    setTimeOnTaskSeconds,
    setLockBrowser,
    lockBrowser,
    setPhotoMathBlock,
    photoMathBlock,
    calculatorEnabled,
    setCalculatorEnabled,
    calculatorType,
    setCalculatorType,
    setUseProtractor,
    setUseRuler,
    isTimed,
    setIsTimed,
    studentFileUpload,
    setAutoStartCRUponGrading,
    autoStartCRUponGrading,
    setInitialFromValue,
    setInitialToValue,
    initialToValue,
    initialFromValue,
    startDate,
    endDate,
    setRetakesEnabled,
    setMasteryValue,
    setRetakeLimitValue,
    setRetakeAvailabilty,
    setRetakeTimedMinute,
    setRetakeTimedHour,
    setRetakeStartDate,
    setRetakeEndDate,
    setIncludeCR,
    allowStudentToPrintPdf,
    setAllowStudentToPrintPdf
  } = useAssignmentContext();

  const studentTools = [
    {
      id: 'calculatorEnabled',
      description: 'Allow students to use a calculator',
      checked: calculatorEnabled == 1 ? true : false,
      onChangeEvent: setCalculatorEnabled,
      visible: true,
      additionalHtml: (
        <div className={`mt-2 mb-4 ${calculatorEnabled ? '' : 'd-none'}`}>
          <Form.Group style={{ width: '45%' }}>
            <Form.Label style={{ fontSize: '0.85em', fontWeight: '500' }}>
              Calculator Type
            </Form.Label>
            <Form.Select
              value={calculatorType}
              onChange={(e) => setCalculatorType(parseInt(e.target.value))}>
              <option value="1">4-function</option>
              <option value="2">Scientific</option>
            </Form.Select>
          </Form.Group>
        </div>
      )
    },
    {
      id: 'textToSpeech',
      description: 'Disable Text-to-Speech',
      checked: textToSpeech,
      visible: disabledTextSpeach,
      onChangeEvent: setTextToSpeech
    },
    {
      id: 'allowStudentsToUploadFiles',
      description: 'Allow Students to Upload Files',
      checked: studentFileUpload,
      onChangeEvent: setStudentFileUpload,
      visible: true
    },
    {
      id: 'allowStudentsToPrintPdf',
      description: 'Allow Students to Print PDF',
      checked: allowStudentToPrintPdf,
      onChangeEvent: setAllowStudentToPrintPdf,
      visible: showStudentPrinting
    },
    {
      id: 'allowLangChange',
      description: 'Allow Changing of display language',
      infoNote: 'This is a computer translation and is not 100% accurate',
      checked: allowLangChange,
      onChangeEvent: setAllowLangChange,
      visible: true
    }
  ];

  const additionalOptions = [
    {
      id: 'timed',
      description: 'Timed',
      visible: true,
      checked: isTimed,
      onChangeEvent: setIsTimed,
      additionalHtml: (
        <div
          className={`d-flex flex-row justify-content-evenly mt-2 mb-4 ${isTimed ? '' : 'd-none'}`}>
          <Form.Group style={{ width: '45%' }}>
            <Form.Label style={{ fontSize: '0.85em', fontWeight: '500' }}>Hour</Form.Label>
            <Form.Select value={timedHour} onChange={(e) => setTimedHour(e.target.value)}>
              {Array.from({ length: 24 }, (_, i) => (
                <option key={`timed-hour-option-${i}`} value={i}>
                  {i}
                </option>
              ))}
            </Form.Select>
          </Form.Group>
          <Form.Group style={{ width: '45%' }}>
            <Form.Label style={{ fontSize: '0.85em', fontWeight: '500' }}>Minutes</Form.Label>
            <Form.Select value={timedMinute} onChange={(e) => setTimedMinute(e.target.value)}>
              {Array.from({ length: 60 }, (_, i) => (
                <option key={`timed-minute-option-${i}`} value={i}>
                  {i}
                </option>
              ))}
            </Form.Select>
          </Form.Group>
        </div>
      )
    },
    {
      id: 'randomized',
      description: 'Randomize Question Order',
      onChangeEvent: setRandomized,
      checked: randomized,
      visible: randomizedQuestion
    },
    {
      id: 'timeOnTask',
      description: (
        <div>
          Track time spent on task using{' '}
          <span style={{ color: '#f7931e' }}>{timeOnTaskSeconds}</span> seconds as timeout period
        </div>
      ),
      checked: timeOnTaskEnabled,
      onChangeEvent: setTimeOnTaskEnabled,
      visible: true,
      additionalHtml: (
        <div
          className={`mx-auto mt-2 mb-4 sliding-bar pb-3 ${timeOnTaskEnabled ? '' : 'd-none'}`}
          style={{ width: '90%' }}>
          <Slider
            min={60}
            max={180}
            value={timeOnTaskSeconds}
            onChange={(e) => setTimeOnTaskSeconds(e)}
            marks={{ 60: 60, 180: 180 }}
          />
        </div>
      )
    },
    {
      id: 'lockBrowser',
      description: 'Enable Browser Lockdown',
      visible: true,
      infoNote: (
        <div>This feature is in detention for acting out! Check back later!</div>
      ),
      // infoNote: (
      //   <div>
      //     Note that the student&apos; s device must have castle&apos; s chrome lockdown extension
      //     installed in order to use this option. Learn more here:{' '}
      //     <a
      //       target="_blank"
      //       rel="noreferrer"
      //       href="https://cl.castlelearning.com/Review/Documents/Browser%20Lockdown%20Guide.pdf"
      //       className="AOPLink">
      //       Browser Lockdown Guide
      //     </a>
      //   </div>
      // ),
      onChangeEvent: setLockBrowser,
      checked: lockBrowser
    },
    {
      id: 'photoMathBlock',
      visible: true,
      description: 'Enable Photomath app blocker',
      infoNote:
        'Note that only equations in Castle Content and those created using the equation editor in PCS Will be blocked',
      onChangeEvent: setPhotoMathBlock,
      checked: photoMathBlock
    },
    {
      id: 'autoStartCRUponGrading',
      visible: true,
      description: `Auto-start ${isAssessment ? 'assessment' : 'assignments'} for students upon grading CR.`,
      onChangeEvent: setAutoStartCRUponGrading,
      checked: autoStartCRUponGrading
    }
  ];

  const onChangeInitialFrom = (value) => {
    if (value === null) {
      setStartDate();
      setInitialFromValue();
    } else {
      const formattedDate = moment(value).format('YYYY/MM/DD h:mm A');
      setStartDate(formattedDate);
      setInitialFromValue(value);
    }
  };

  const onChangeInitializeToFrom = () => {
    var fromDateTime = new Date();
    fromDateTime.setHours(1);
    fromDateTime.setMinutes(0);
    var toDateTime = new Date();
    toDateTime.setHours(23);
    toDateTime.setMinutes(59);
    setInitialFromValue(fromDateTime);
    setInitialToValue(toDateTime);
  };

  const onChangeInitialTo = (value) => {
    if (value === null) {
      setEndDate();
      setInitialToValue();
    } else {
      const formattedDate = moment(value).format('YYYY/MM/DD h:mm A');
      setEndDate(formattedDate);
      setInitialToValue(value);
    }
  };

  useEffect(() => {
    if (!isSelfAssign) LoadDefaultPersistValues();
  }, [isSelfAssign]);

  useEffect(() => {
    setStartDate(initialFromValue);
    setEndDate(initialToValue);
  }, [initialFromValue, initialToValue]);

  const handleAllowAnswerChange = () => {
    setAllowAnswerChange(true);
    setShowGrade(false);
  };

  const handleShowGradeChange = () => {
    setAllowAnswerChange(false);
    setShowGrade(true);
  };

  const getAssignmentSettings = async()=>{
    const persistData = await getAssignmentSettingsApi().unwrap();
    setAllowAnswerChange(persistData.allowAnswerChange);
    setAssignmentMode(persistData.assignmentMode);
    setRetakesEnabled(false); 
    setAllowStudentToPrintPdf(false);
    
    if(!allowOfflineMode && persistData.availability == 'offline')
    {
      setAvailabilityOption('Always');
    }else if(persistData.availability == 'FromTo') {
      onChangeInitializeToFrom();
    }

    setAvailabilityOption(persistData.availability);

    setShowGrade(persistData.showGrade);
    setRandomized(persistData.randomized);
    setTextToSpeech(persistData.textToSpeech);
    setAutoLockQuestions(persistData.autoLockQuestions);
    setAllowLangChange(persistData.allowLangChange);
    setCalculatorEnabled(persistData.useCalc);
    setUseProtractor(persistData.useProtractor);
    setUseRuler(persistData.useRuler);
    setTimeOnTaskEnabled(persistData.timeOnTask);
    setLockBrowser(persistData.lockBrowser);
    setPhotoMathBlock(persistData.photoMathBlock);
    setAutoStartCRUponGrading(persistData.autoStartCRUponGrading);
    setTimeOnTaskSeconds(persistData.period);
    setStudentFileUpload(persistData.studentFileUpload);

    const timedHours = Math.floor(persistData.timeSpan / 60);
    const timedMinutes = persistData.timeSpan % 60;
    setTimedMinute(timedMinutes);
    setTimedHour(timedHours);
    setIsTimed(persistData.timed);
    setCalculatorType(persistData.calculatorType);
  };

  const handleReset = () => {
    setAllowAnswerChange(false);
    setAssignmentMode('');
    setRetakesEnabled(false);
    setShowGrade(false);
    setRandomized(false);
    setTextToSpeech(false);
    setAutoLockQuestions(false);
    setAllowLangChange(false);
    setCalculatorEnabled(true);
    setUseProtractor(1);
    setUseRuler(1);
    setTimeOnTaskEnabled(false);
    setLockBrowser(false);
    setPhotoMathBlock(false);
    setRetakeAvailabilty('');
    setAutoStartCRUponGrading(false);
    setStartDate('');
    setEndDate('');
    setTimeOnTaskSeconds(60);
    setStudentFileUpload(false);
    setRetakeTimedHour(0);
    setRetakeTimedMinute(0);
    setRetakeStartDate('');
    setRetakeEndDate('');
    setTimedMinute(0);
    setTimedHour(0);
    setIsTimed(false);
    setCalculatorType(1);
    setAvailabilityOption('Always');
    setMasteryValue(80);
    setRetakeLimitValue(1);
    setIncludeCR(false);
    setAllowStudentToPrintPdf(false);
  };

  const LoadDefaultPersistValues = () => {
    getAssignmentSettings();
    if (presetSettingsCallback) {
      presetSettingsCallback();
    }
  };

  return (
    <Col
      className={
        noColumn ? 'assign-box' : `${twoColumn ? 'col-lg-12' : 'assign-box col-lg-6'} p-20`
      }>
      <div className="m-3">
        <div className="assign-box-header">
          <div className='align-items-center d-flex gap-2 mb-2'>
            <span className="fs-5 fw-500">{isAssessment ? 'Assessment' : 'Assignment'} Settings</span>
            {!isSelfAssign &&
            <OverlayTrigger
              key={'assignment-options'}
              placement="right"
              overlay={
                <Tooltip className="initial-assignment-tooltip">
                  <div>Reset to default</div>
                </Tooltip>
              }>
              <button className='btn d-flex fa-lg m-0 px-2 reset-btn' onClick={()=>handleReset()}> <FontAwesomeIcon icon={faRotateRight} /> </button>
            </OverlayTrigger>
            }
          </div>
          <div className={`assign-settings ${headingDescription ? '' : 'd-none'}`}>
            <div className="assign-settings-header my-2">
              {isAssessment ? 
                'Assign to checked students using the following options'
                :
                'Assign to self and/or checked students using the following options'
              }
            </div>
          </div>
        </div>
        <div className={`assign-box-content ${noColumn && 'assignsettingsHeight'} ${twoColumn ? 'd-flex' : ''}`}>
          <div className={`${twoColumn ? 'col-6' : ''}`}>
            <div className="d-flex gap-2 my-3 align-items-center">
              <div className="assign-options">Initial {isAssessment ? 'Assessment' : 'Assignment'} Options</div>
              <OverlayTrigger
                key={'assignment-options'}
                placement="left"
                overlay={
                  <Tooltip className="initial-assignment-tooltip">
                    <div>
                      <div className='px-3 text-start'>
                        <p>
                          The <strong>Mode</strong> indicates how students can interact
                          with the {isAssessment ? ' assessment' : ' assignment'}.
                        </p>
                        <div>
                          <ul>
                            <li>
                              <strong>Open</strong>: allows students full access to all questions,
                            with all available feedback.
                            </li>
                            <li>
                              <strong>Quiz</strong>: students can answer the questions but do not get
                            any feedback.
                            </li>                        
                          </ul>
                        </div>
                        <p>
                          The <strong>Availability Options</strong> controls when a student has
                          access to the {isAssessment ? ' assessment' : ' assignment'}
                        </p>
                        <div>
                          <ul>
                            <li>
                              <strong>Always:</strong> allows students full access to all questions at
                              all times.
                            </li>
                            <li>
                              <strong>From/To:</strong> allows students to work on the {isAssessment ? 'assessment ' : 'assignment '}
                              within a date and time range.
                            </li>
                            <li>
                              <strong>Offline</strong>: Locked to students who work on the {isAssessment ? 'assessment ' : 'assignment '}
                              offline. Student answers uploaded by the teacher. Only available if the 
                              {isAssessment ? ' assessment' : ' assignment'} contains just multiple choice questions.
                            </li>
                          </ul>
                        </div>
                        <p>
                          The <strong>Timed</strong> option allows the student to work on an
                          {isAssessment ? ' assessment' : ' assignment'} for the specified amount of time. Timer starts when the student
                          opens the {isAssessment ? 'assessment' : 'assignment'}.
                        </p>
                      </div>
                    </div>
                  </Tooltip>
                }>
                <FontAwesomeIcon className="circle-question" icon={faCircleQuestion} />
              </OverlayTrigger>
            </div>

            <div className="assign-settings-header">
              Availability Options
              <span className={`required ${availabilityRequired ? '' : 'd-none'}`}>*</span>
            </div>

            <Form.Group className="mt-1" id="availabilityRadioButtonGroup">
              <Form.Check
                inline
                label="Always"
                name="availabilityRadioGroup"
                type="radio"
                id="alwaysAvailabilityRadio"
                checked={availabilityOption == 'Always'}
                onChange={() => setAvailabilityOption('Always')}
              />
              <Form.Check
                inline
                label="From/To"
                name="availabilityRadioGroup"
                type="radio"
                id="frontToAvailabilityRadio"
                checked={availabilityOption == 'FromTo'}
                onChange={() => {setAvailabilityOption('FromTo'); onChangeInitializeToFrom();}}
              />
              {allowOfflineMode && (
                <Form.Check
                  inline
                  label="Offline"
                  name="availabilityRadioGroup"
                  type="radio"
                  id="offlineAvailabilityRadio"
                  checked={availabilityOption == 'offline'}
                  onChange={() => setAvailabilityOption('offline')}
                />
              )}
            </Form.Group>

            {availabilityOption == 'FromTo' && (
              <div className="d-flex gap-5 align-items-center justify-content-between col-10">
                <div className="col-6">
                  <div className="my-3 assign-settings-header">From</div>
                  <div className="date-time-picker">
                    <DateTimePicker
                      onChange={onChangeInitialFrom}
                      value={startDate}
                      format="MM/dd/yyyy h:mm a"
                      className="d-flex"
                      calendarClassName="s-date-calendar scale-6"
                      id="initial-from-date-picker"
                      disableClock={true}
                    />
                  </div>
                </div>
                <div className="col-6">
                  <div className="my-3 assign-settings-header">To</div>
                  <div className="date-time-picker">
                    <DateTimePicker
                      onChange={onChangeInitialTo}
                      value={endDate}
                      format="MM/dd/yyyy h:mm a"
                      className="d-flex"
                      calendarClassName="s-date-calendar scale-6"
                      id="initial-to-date-picker"
                      disableClock={true}
                    />
                  </div>
                </div>
              </div>
            )}

            {availabilityOption !== 'offline' && (
              <div>
                <div className="assign-settings-header mt-3">
                  Modes
                  <span className={`required ${modesRequired ? '' : 'd-none'}`}>*</span>
                </div>
                <div>
                  <Form.Group className="mb-3 mt-1" id="modeRadioButtonGroup">
                    <Form.Check
                      inline
                      label="Open"
                      name="modeRadioGroup"
                      type="radio"
                      checked={assignmentMode == 'open'}
                      id="openModeRadio"
                      onChange={() => {
                        setAssignmentMode('open');
                        setRetakesEnabled(false);
                      }}
                    />
                    <Form.Check
                      inline
                      label="Quiz"
                      name="modeRadioGroup"
                      type="radio"
                      checked={assignmentMode == 'quiz'}
                      id="quizModeRadio"
                      onChange={() => {
                        setAssignmentMode('quiz');
                      }}
                    />
                    {/*showBetaButton &&
                  <span>
                    <Form.Check inline id='ezScoreCamModeRadio' >
                      <Form.Check.Input type='radio' name='modeRadioGroup' onChange={() => setAssignmentMode('gradecam')}  />
                      <Form.Check.Label style={{ color: '#09BCF3' }}>EzScoreCam</Form.Check.Label>
                    </Form.Check>
                    <OverlayTrigger
                      key="EZScroreCamBeta"
                      placement="top"
                      overlay={
                        <Tooltip id="betaTip2" className="beta-tooltip">
                          <p className="mb-0">
                            This new feature is in Beta. Please report any issues or feedback you
                            may have to <span>support@CastleLearning,com</span> so that we can
                            continue to improve your experience.{' '}
                          </p>
                        </Tooltip>
                      }>
                      <span className="beta p-1">BETA</span>
                    </OverlayTrigger>
                  </span>*/}
                  </Form.Group>
                </div>

                {assignmentMode == 'open' && 
                <>
                  <Form.Check
                    type="checkbox"
                    label="Auto-lock questions"
                    id="autoLockQuestionsCheck"
                    checked={autoLockQuestions}
                    onChange={(e) => setAutoLockQuestions(e.target.checked)}
                  />
                  {allowRetakesOption && 
                    <AssignmentRetakeSettings/>}
                </>
                }

                {assignmentMode == 'quiz' && (
                  <div>
                    <Form.Check
                      type="radio"
                      label="Allow changes to answers"
                      id="allowAnswerChangesCheck"
                      name='displayGradeAndAndAllowLangChange'
                      checked={allowAnswerChange}
                      onChange={handleAllowAnswerChange}
                    />
                    <Form.Check
                      type="radio"
                      label="Display grade upon completion of quiz"
                      id="displayGradeOnCompletionCheck"
                      name='displayGradeAndAndAllowLangChange'
                      checked={showGrade}
                      onChange={handleShowGradeChange}
                    />
                  </div>
                )}
              </div>
            )}
          </div>

          {availabilityOption !== 'offline' && (
            <div className={`${twoColumn ? 'col-6' : ''}`}>
              <div className="assign-options mt-4">Student Tools</div>
              {studentTools.map((item, index) => {
                if (!item.visible) return;
                return (
                  <>
                    <Form.Check key={`student-tool${index}`} id={`${item.id}Checkbox`}>
                      <Form.Check.Input
                        type="checkbox"
                        checked={item.checked}
                        onChange={(e) => item.onChangeEvent(e.target.checked)}
                      />
                      <Form.Check.Label className="ms-2">
                        {item.description}
                        {item.infoNote && (
                          <OverlayTrigger
                            key={`student-tool-tip${index}`}
                            placement="top"
                            overlay={
                              <Tooltip className="custom-tooltip" id={item.id}>
                                {item.infoNote}
                              </Tooltip>
                            }>
                            <FontAwesomeIcon className="circle-question ms-2" icon={faCircleInfo} />
                          </OverlayTrigger>
                        )}
                      </Form.Check.Label>
                    </Form.Check>
                    {item.additionalHtml}
                  </>
                );
              })}

              <div className="my-4 d-flex gap-2 align-items-center assign-settings-header">
                <div className="assign-options">Additional Options</div>
                <OverlayTrigger
                  key={'assignment-options'}
                  placement="right"
                  overlay={
                    <Tooltip className="additional-options-tooltip">
                      <div>
                        <p>
                          The <strong>Auto-Lock</strong> option allows students to answer questions
                          with all available feedback, but they cannot view questions that they have
                          already answered.
                        </p>
                        <br />
                        <p>
                          If checked, the <strong>Allow changing of language display allows</strong>{' '}
                          will allow students to change the display language from English to other
                          languages for this {isAssessment ? 'assessment' : 'assignment'}. The Google translate feature provides a
                          basic translation of a question, hint, reason, or vocabulary. In some
                          cases, the translation may misinterpret a word, phrase, idioms,
                          colloquialisms or slang. It may also have difficulty with context, figures
                          of speech, and technical terminology. Note that the {isAssessment ? 'assessment' : 'assignment'} will
                          automatically be displayed using numbers for multiple choice questions.
                        </p>
                        <br />
                        <p>
                          <strong>Display grade upon completion of quiz:</strong> allows students to
                          see their grade when they complete a quiz mode {isAssessment ? 'assessment' : 'assignment'}.
                        </p>
                      </div>
                    </Tooltip>
                  }>
                  <FontAwesomeIcon className="circle-question" icon={faCircleQuestion} />
                </OverlayTrigger>
              </div>

              {additionalOptions.map((item, index) => {
                if (!item?.visible) return;
                return (
                  <>
                    <Form.Check key={`additional-options${index}`} id={`${item.id}Checkbox`}>
                      {item.description == 'Enable Browser Lockdown' ? (
                        <>
                          <Form.Check.Input
                            type="checkbox"
                            checked={item.checked}
                            disabled
                            onChange={(e) => item.onChangeEvent(e.target.checked)}
                          />
                          <Form.Check.Label className="ms-1 position-relative">
                            <OverlayTrigger
                              key={`additional-option-tip${index}`}
                              placement="top"
                              overlay={
                                <Tooltip className="custom-tooltip" id={item.id}>
                                  {item.infoNote}
                                </Tooltip>
                              }>
                              <span >{item.description}</span>
                            </OverlayTrigger>
                            {/* <span
                              className="lockdown-tooltip-text"
                              onMouseEnter={handleMouseEnter}
                              onMouseLeave={handleMouseLeave}>
                              <FontAwesomeIcon className="circle-question ms-2" icon={faCircleInfo} />
                            </span>
                               {isHovering && (
                              <div
                                onMouseEnter={handleMouseEnter}
                                onMouseLeave={handleMouseLeave}
                                className="tooltip-content lockdown-tooltip">
                                <span>{item.infoNote}</span>
                              </div>
                            )} */}
                          </Form.Check.Label>
                        </>
                      ) : (
                        <>
                          <Form.Check.Input
                            type="checkbox"
                            checked={item.checked}
                            onChange={(e) => item.onChangeEvent(e.target.checked)}
                          />
                          <Form.Check.Label className="ms-1">
                            {item.description}
                            {item.infoNote && (
                              <OverlayTrigger
                                key={`additional-option-tip${index}`}
                                placement="top"
                                overlay={
                                  <Tooltip className="custom-tooltip" id={item.id}>
                                    {item.infoNote}
                                  </Tooltip>
                                }>
                                <FontAwesomeIcon
                                  className="circle-question ms-2"
                                  icon={faCircleInfo}
                                />
                              </OverlayTrigger>
                            )}
                          </Form.Check.Label>
                        </>
                      )}
                    </Form.Check>

                    {item.additionalHtml}
                  </>
                );
              })}
            </div>
          )}

          {availabilityOption === 'offline' && (
            <div className={`${twoColumn ? 'col-6' : ''}`}>
              <div className="assign-options mt-4">Student Tools</div>
              <>
                <Form.Check key={'student-tool-offline'} id={'allowStudentsToUploadFiles-Checkbox'}>
                  <Form.Check.Input
                    type="checkbox"
                    checked={studentFileUpload}
                    onChange={(e) => setStudentFileUpload(e.target.checked)}
                  />
                  <Form.Check.Label className="ms-2">
                    Allow Students to Upload Files
                  </Form.Check.Label>
                </Form.Check>
              </>
            </div>
          )}
        </div>
      </div>
    </Col>
  );
};

Index.propTypes = {
  allowRetakesOption: PropTypes.bool,
  allowOfflineMode: PropTypes.bool,
  twoColumn: PropTypes.bool,
  headingDescription: PropTypes.bool,
  availabilityRequired: PropTypes.bool,
  modesRequired: PropTypes.bool,
  noColumn: PropTypes.bool,
  randomizedQuestion: PropTypes.bool,
  disabledTextSpeach: PropTypes.bool,
  presetSettingsCallback: PropTypes.func,
  isSelfAssign: PropTypes.bool,
  isAssessment: PropTypes.bool,
  showStudentPrinting: PropTypes.bool
};
export default Index;
